import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './useAuth';

export interface Suscripcion {
  status: 'trial' | 'active' | 'expired' | 'canceled';
  access_until: string;
  trial_extended: boolean;
}

/**
 * Estado de acceso del usuario: hasta cuándo puede usar la app.
 *
 * La app SOLO LEE esto. La suscripción no se puede escribir desde el
 * navegador —no hay permiso para hacerlo— y se modifica únicamente
 * desde la función `canjear_codigo` de la base, que valida antes.
 *
 * Si pudiera escribirse desde acá, cualquiera se pondría diez años de
 * acceso con una línea en la consola del navegador.
 */
export const useSuscripcion = () => {
  const { user, loading: cargandoSesion } = useAuth();
  const [suscripcion, setSuscripcion] = useState<Suscripcion | null>(null);
  const [loading, setLoading] = useState(true);

  const cargar = useCallback(async () => {
    if (!user) {
      setSuscripcion(null);
      setLoading(false);
      return;
    }
    const { data, error } = await supabase
      .from('subscriptions')
      .select('status, access_until, trial_extended')
      .maybeSingle();

    if (error) console.error('Error leyendo la suscripción:', error);
    setSuscripcion(data ?? null);
    setLoading(false);
  }, [user]);

  useEffect(() => {
    if (!cargandoSesion) cargar();
  }, [cargar, cargandoSesion]);

  const vence = suscripcion ? new Date(suscripcion.access_until) : null;
  const ahora = new Date();

  // Días completos que faltan. Se redondea hacia arriba para que el
  // último día cuente como 1 y no como 0: a nadie le sirve leer "te
  // quedan 0 días" cuando todavía puede usarla hoy.
  const diasRestantes = vence
    ? Math.max(0, Math.ceil((vence.getTime() - ahora.getTime()) / 86400000))
    : 0;

  const tieneAcceso = Boolean(
    vence &&
    vence > ahora &&
    (suscripcion?.status === 'trial' || suscripcion?.status === 'active')
  );

  return {
    suscripcion,
    loading: loading || cargandoSesion,
    tieneAcceso,
    diasRestantes,
    venceEl: vence,
    // Se avisa desde 5 días antes: suficiente para que le dé tiempo de
    // ir a buscar el código, sin molestar desde el primer día.
    porVencer: tieneAcceso && diasRestantes <= 5,
    yaCanjeo: Boolean(suscripcion?.trial_extended),
    refrescar: cargar,
  };
};
